import { useEffect, useRef, useState } from 'react'
import { X, Loader2, Receipt, Banknote, Smartphone, Store as StoreIcon, Users, Minus, Plus, ChevronLeft } from 'lucide-react'
import { useStore, type CheckoutConfirmResult, type Order, type Store } from '../store/useStore'
import AutoTopUpSheet from './AutoTopUpSheet'
import PayMethodToggle from './PayMethodToggle'
import { topupBreakdownFor, topupParamsFor } from '../lib/pointsTopup'
import type { PointsCheckoutResult } from '../services/api/orders'
import { useCheckoutStyle } from '../hooks/useCheckoutStyle'
import { useI18n } from '../i18n/useI18n'
import { api, ApiError } from '../services/api'
import { getGuestId } from '../lib/guestId'
import { showToast } from './Toast'
import StripeCheckout from './StripeCheckout'
import { useNavigate } from 'react-router-dom'
import { displayOrderNumber } from '../lib/orderNumber'
import { storePaymentOptions } from '../lib/storePayment'
import { paymentDeclineI18nKey, isCashOnlyStoreError, isRetryable } from '../lib/apiErrorMessage'
import { itemNameLines } from '../lib/itemName'
import VariationLine from './VariationLine'
import type { VariationSnapshot } from '../lib/menuVariation'
import { PAYMENT_METHOD_POINTS } from '../lib/paymentMethods'

interface CheckoutSheetProps {
  open: boolean
  storeId: string
  storeName: string
  unpaidOrders: Order[]
  onClose: () => void
  /**
   * Called after a successful payment. Default navigation is to
   * /payment/complete which handles the receipt + review/share flow.
   */
  onPaid?: () => void
  /**
   * #972 アプリ内カード決済 (Stripe) が使えるか。現金のみ運用店では false を渡し、
   * カード選択を隠して現金会計のみにする。
   * #1801 未指定なら店を取得して判定する (取得が終わるまで支払手段は出さない。
   * ご注文状況の画面は店の情報を持たずに開くため)。
   */
  cardInAppAllowed?: boolean
  /**
   * #2344 この伝票が**払い終えた**か (店がレジで確定した)。レジで待っている間に
   * 確定されることがあり、そのまま出しておくと「レジでお支払いください ¥4,200」が
   * 残り続ける。呼び出し元が「伝票が消えた = 払い終えた」と決めないのは、
   * 店が品を全部取り消しても伝票は消えるため (払っていない客に完了を出さない)。
   */
  billSettled?: boolean
}

// 会計シートの注文ステータスバッジ色（MyOrders と揃える）。会計対象は未払いの
// 進行中注文なので received/preparing/served/pending_approval が主。
const CHECKOUT_STATUS_COLOR: Record<string, string> = {
  pending_approval: 'bg-purple-100 text-purple-700',
  received: 'bg-amber-100 text-amber-700',
  preparing: 'bg-blue-100 text-blue-700',
  served: 'bg-emerald-100 text-emerald-700',
}

// #1535 伝票リストの状態グループの並び。未提供 (受付/調理中) を上に置き、
// 「まだ来ていないもの」を会計前に確認しやすくする。
const BILL_STATUS_ORDER = ['pending_approval', 'received', 'preparing', 'served']

// #1535 注文時刻 (HH:MM)。「頼んだ/頼んでない」の揉め防止用の記録。
function formatOrderTime(iso: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

// #1044 割り勘表示 — 決済フローには影響しない、表示・計算のみなので実際の分割決済は行わない。
const SPLIT_COUNT_MIN = 2
const SPLIT_COUNT_MAX = 20

// #2190 verifying = カードは通ったが確定が届かない (やり直している / やり直せなかった)。
// duplicate = 先にほかの支払いで払われていた (この決済はカードに返金する)
type Stage = 'review' | 'method' | 'payment' | 'confirming' | 'verifying' | 'duplicate' | 'cashGuide'

/**
 * #2190 確定のやり直しの間隔。確定は同じ決済で何度呼んでも 1 回分 (backend の「アプリのカード会計の確定」)
 * なので、カードの部品には戻さず (戻すとカード欄が空になり、払い直しに見える)、同じ決済で確定をやり直す。
 * 画面が確定できなくても、webhook が数秒で注文を払い済みにする
 */
const CONFIRM_RETRY_DELAYS_MS = [2000, 4000, 8000]
/** #1805 アプリで払う場合の手段。カードも YOKOSO 通貨もアプリ内で完結する */
type InAppMethod = 'card' | 'points'
// #956 Phase 2: pay only the customer's own bill, or the whole table's.
type PayScope = 'self' | 'table'

/**
 * Bill / checkout sheet for self-order (#289).
 *
 * Triggered by the "お会計" button on StoreDetail when the customer has at
 * least one unpaid order. Sums all unpaid orders for the current store and
 * settles them in a single Stripe PaymentIntent.
 */
export default function CheckoutSheet({
  open, storeId, storeName, unpaidOrders, onClose, onPaid, cardInAppAllowed: cardInAppAllowedProp,
  billSettled = false,
}: CheckoutSheetProps) {
  const navigate = useNavigate()
  const { t } = useI18n()
  const { userId, stores, wallet, restoreSession, paySelfOrderCheckout, payTableCheckout } = useStore()

  const [stage, setStage] = useState<Stage>('review')
  // #2190 確定をやり直している決済・やり直しても確定できなかったか・返金する額 (重複のとき)
  const [confirmingPi, setConfirmingPi] = useState<string | null>(null)
  const [verifyFailed, setVerifyFailed] = useState(false)
  // #2190 項目 7: カードの部品が `processing` を見ている間 (閉じると見張りが止まる)
  const [cardBusy, setCardBusy] = useState(false)
  // #2190 確定のやり直しの途中で画面を離れたら、やり直しをやめて画面を動かさない (webhook が払い済みにする)
  const alive = useRef(true)
  useEffect(() => {
    alive.current = true
    return () => { alive.current = false }
  }, [])
  const [duplicateAmount, setDuplicateAmount] = useState(0)
  // #1805 「アプリで支払う / レジで支払う」→ アプリなら「カード / YOKOSO 通貨」の 2 段。
  // 以前は 3 つを横並びにしていたが、レジ払いだけ性質が違う (店頭で現金を渡す) ので、
  // 同じ列に置くと客が「アプリで払う」という判断を 2 回させられていた。
  //
  // **2 段目は画面を分ける。** 同じ画面に 2 段を積むと、赤 (1 段目) と紺 (2 段目) が
  // 同時に光って「4 つの中から 1 つ選ぶ」ように見え、どちらが効いているのか読めない。
  // 1 段目は選択ではなく**行き先**にして、選んだ先で 2 段目だけを見せる。
  // #1801 現金のみ運用と分かった店。画面の店情報が古いと「アプリで支払う」が出て
  // しまうので、backend が 409 で断ったらこちらで閉じる (押すたびに同じ 409 を
  // 踏ませない)
  const [cashOnlyDetected, setCashOnlyDetected] = useState(false)
  const [inAppMethod, setInAppMethod] = useState<InAppMethod>('card')
  // #1800 ポイント残高が足りないときの「不足分をチャージして払う」承認シート (#1190)
  const [showAutoTopUp, setShowAutoTopUp] = useState(false)
  const [scope, setScope] = useState<PayScope>('self')
  const [submitting, setSubmitting] = useState(false)
  // #1044 割り勘表示 — トグル ON で人数入力 + 1人あたり金額をリアルタイム表示する。
  const [splitEnabled, setSplitEnabled] = useState(false)
  const [splitCount, setSplitCount] = useState(2)
  const [session, setSession] = useState<{
    clientSecret: string
    paymentIntentId: string
    connectedAccountId: string | null
    totalAmount: number
    orderIds: string[]
    isTable: boolean
    tableNumber?: string
    /** #1535 この支払いに含まれない店側起票 (店員入力・放題プラン) の未払い額。
     * 黙って除外すると客は「全部払えた」と誤解し、店はレジでの回収を忘れる */
    excludedStoreEnteredTotal?: number
  } | null>(null)
  // #956 Phase 2: combined unpaid total for the whole table (all accounts).
  // #1535 内訳 (myTotal/othersTotal/staffTotal/otherOrders) はサーバが正 —
  // 「自分の分」とテーブル全体の差が画面内で説明されるようにする。
  const [tableBill, setTableBill] = useState<{
    totalAmount: number; orderCount: number; orderIds: string[]; hasStaffOrders?: boolean
    myTotal?: number; othersTotal?: number; staffTotal?: number
    // #2155 自動のお通し・席料と、選んだ範囲で実際に払う額 (お通しは卓を締める支払いに載る)
    autoExtras?: { presetId?: string | null; name: string; price: number; quantity: number }[]
    autoExtrasTotal?: number; tablePayable?: number; myPayable?: number
    otherOrders?: {
      id: string; totalAmount: number; staffEntered?: boolean
      status?: string; callNumber?: string; createdAt?: string
      items: { name: string; quantity: number; variations?: VariationSnapshot[] }[]
    }[]
  } | null>(null)

  // #2155 レジで払うと決めた後に見せる額。サーバが返した額 (受け取る額) を出す
  const [cashAmount, setCashAmount] = useState<number | null>(null)
  // #2155 払う瞬間に額が変わっていたら (同席者が先に払った等)、卓の額を読み直し、
  // サーバが返した額 (受け取る額) を出す。次はその額で払う (手元の額のままだと何度でも断られる)
  const [tableBillNonce, setTableBillNonce] = useState(0)
  const [serverTotal, setServerTotal] = useState<number | null>(null)
  // 払う範囲 (自分の分 / テーブル全員分) を変えたら、サーバが返した前の額は使わない
  useEffect(() => { setServerTotal(null) }, [scope])

  // The table this customer is seated at — their orders carry the number.
  const tableNumber = unpaidOrders.find((o) => o.tableNumber)?.tableNumber

  // Reset transient state every time the sheet is opened/closed.
  useEffect(() => {
    if (!open) {
      setStage('review')
      setConfirmingPi(null)
      setVerifyFailed(false)
      setDuplicateAmount(0)
      setCashOnlyDetected(false)
      setInAppMethod('card')
      setScope('self')
      setSession(null)
      setTableBill(null)
      setCashAmount(null)
      setServerTotal(null)
      setSplitEnabled(false)
      setSplitCount(2)
      setShowAutoTopUp(false)
    }
  }, [open])

  // #956 Phase 2: when seated at a table, fetch the table-wide unpaid total so
  // we can offer to settle everyone's bill at once.
  useEffect(() => {
    // #1398 ゲストも卓に着いていれば卓の合計を見られる (サーバが着席を検証する)
    if (!open || !tableNumber || (!userId && !getGuestId())) return
    let cancelled = false
    api.getTableBill(storeId, tableNumber, userId || undefined)
      .then((res) => {
        if (!cancelled && res && typeof res.totalAmount === 'number') setTableBill(res)
      })
      .catch(() => { /* ignore — the table option just won't appear */ })
    return () => { cancelled = true }
  }, [open, userId, tableNumber, storeId, tableBillNonce])

  // #2344 「レジで払う」と申告したあとも、店は品を取り消す。自分の注文は
  // 画面が realtime で受け取るが、同卓のほかの人の分とお通しはサーバが持つので、
  // 待っている間だけ卓の伝票を読み直す (待ち時間は数分なので 10 秒で足りる)。
  useEffect(() => {
    if (!open || stage !== 'cashGuide' || !tableNumber) return
    const id = window.setInterval(() => setTableBillNonce((n) => n + 1), 10_000)
    return () => window.clearInterval(id)
  }, [open, stage, tableNumber])

  // #2344 店がレジで会計を確定したら、この画面は役目を終える。閉じないと
  // 「レジでお支払いください ¥4,200」が払った後も残る。
  // **自分の決済の最中は閉じない** — カードの確定中に畳むと、レシートの画面
  // (/payment/complete) へ進めなくなる。待っている / 見ているだけの段階で閉じる。
  // 閉じた先の画面が「お会計が完了しました」のお知らせとレシートへの導線を出す
  // (useOrderReadyNotifier)。
  useEffect(() => {
    if (!open || !billSettled) return
    if (stage !== 'cashGuide' && stage !== 'review' && stage !== 'method') return
    onClose()
  }, [open, billSettled, stage, onClose])

  // #1173/#1178 会計案内スタイル: S700 導入店では「現金」でなく「レジで支払う」、
  // スタッフが伝票を持参する店では「お席でお待ちください」。zustand の stores
  // リストは起動時スナップショットで設定変更が反映されないため、シート表示の
  // たびに store doc を直接取得する (useCheckoutStyle)。hooks 順序を保つため
  // early return より前に置く (open=false の間は fetch しない)。
  const checkoutInfo = useCheckoutStyle(storeId, open)

  if (!open) return null

  const payAtRegister = !!checkoutInfo?.payAtRegister
  const staffBringsBill = !!checkoutInfo?.staffBrings
  // #1801 アプリ内カードの可否。呼び出し元が渡さなければ、useCheckoutStyle が取った店 doc
  // (キャッシュあり) → 手元の店舗一覧 の順に判定する。どちらも無い間は undefined =
  // 支払手段を出さない (取得前 / 失敗時に「現金のみ」と誤って見せない。境界は backend の 409)。
  const listedStore = stores.find((s) => s.id === storeId)
  const cardInAppAllowed: boolean | undefined =
    cardInAppAllowedProp
    ?? checkoutInfo?.cardInApp
    ?? (listedStore ? storePaymentOptions(listedStore).cardInApp : undefined)
  const paymentOptionsKnown = cardInAppAllowed !== undefined
  // #1800 ポイント払い。店が受ける設定 + KYC 済み (現金のみ運用でも可)。判定表は lib/storePayment.ts
  const pointsAllowed: boolean =
    checkoutInfo?.points
    ?? (listedStore ? storePaymentOptions(listedStore).points : false)
  const balance = wallet?.balanceJPYC ?? 0

  const ownTotal = unpaidOrders.reduce((s, o) => s + o.totalAmount, 0)
  // Offer "テーブル全員分" only when there's strictly more to pay than the
  // customer's own bill (= other accounts also have unpaid orders at the table).
  const tableOption = !!tableNumber && !!tableBill && tableBill.totalAmount > ownTotal
  // #977 店員入力の注文は「持ち主」がいないため「自分の分」では精算されない。卓に
  // 店員入力がある場合は「テーブル全員分」に固定して取りこぼしを防ぐ。
  const forceTable = !!tableBill?.hasStaffOrders && tableOption
  const isTable = (forceTable || scope === 'table') && tableOption
  // #2155 お通し・席料は卓を締める支払いに載る。「テーブル全員分」は必ず締め、「自分の分」は
  // この卓の残りが自分の分だけのとき締める。どちらに載るかはサーバ (table-bill) が決める
  const tableExtras = tableBill ? Math.max(0, (tableBill.tablePayable ?? tableBill.totalAmount) - tableBill.totalAmount) : 0
  const selfExtras = tableBill ? Math.max(0, (tableBill.myPayable ?? tableBill.myTotal ?? 0) - (tableBill.myTotal ?? 0)) : 0
  const extrasTotal = isTable ? tableExtras : selfExtras
  const computedTotal = (isTable && tableBill ? tableBill.totalAmount : ownTotal) + extrasTotal
  const total = serverTotal ?? computedTotal
  // #2344 レジで待っている間に見せる額。**申告したときの額に固定しない** — 店が
  // 品を取り消せば請求額は変わるので、固定すると実際と違う額を見て待つことになる
  // (体験で確かめたときは ¥4,200 のまま、実際の会計は ¥2,800 だった)。
  // 卓の伝票を読めているあいだはその計算を出し、読めていない間だけ、申告した
  // ときにサーバが返した額を出す。
  const cashGuideAmount = !tableNumber || tableBill
    ? computedTotal
    : (cashAmount ?? computedTotal)
  // #1805 実際に使う手段。アプリで払えない店 (カードもポイントも不可) はレジに倒す。
  // アプリを選んでいても、選ばれた手段が使えなければ使える方に寄せる
  // (現金のみ運用でポイントだけ受ける店 = カード無しでアプリ払いが成立する)
  const inAppAvailable = (!!cardInAppAllowed || pointsAllowed) && !cashOnlyDetected
  const effectiveInApp: InAppMethod =
    inAppMethod === 'points' || !cardInAppAllowed
      ? (pointsAllowed ? 'points' : 'card')
      : 'card'
  const pointsShortfall = Math.max(0, total - balance)

  // #1535 卓の伝票は 1 つのリストで見せる (自分/友達/店員が入力タグ + 状態)。
  // 「自分の注文」と「ほかの注文」を分けると 卓=1伝票 の直感と合わない。
  // 注文順 (古い順 = 紙伝票と同じ) に並べ、「自分の分」選択時は自分以外を
  // 薄くして支払い対象を示す。
  const otherRows = tableBill?.otherOrders || []
  const showTags = otherRows.length > 0
  const billRows: {
    id: string; mine: boolean; staffEntered: boolean; number: string
    status?: string; totalAmount: number
    items: { name: string; quantity: number; variations?: VariationSnapshot[] }[]; createdAt: string
  }[] = [
    ...unpaidOrders.map((o) => ({
      id: o.id,
      mine: true,
      staffEntered: false,
      number: displayOrderNumber(o),
      status: o.status as string | undefined,
      totalAmount: o.totalAmount,
      items: o.items,
      createdAt: String(o.createdAt || ''),
    })),
    ...otherRows.map((o) => ({
      id: o.id,
      mine: false,
      staffEntered: !!o.staffEntered,
      number: displayOrderNumber({ id: o.id, callNumber: o.callNumber }),
      status: o.status,
      totalAmount: o.totalAmount,
      items: o.items,
      createdAt: String(o.createdAt || ''),
    })),
  ].sort((a, b) => (a.createdAt < b.createdAt ? -1 : 1))

  // #1805 「アプリで支払う」を押したときの行き先。**手段が 1 つしか無い店では
  // 2 段目を出さない** — 選択肢が 1 つの画面を挟むのは操作を増やすだけ。
  const goInApp = () => {
    if (cardInAppAllowed && pointsAllowed) {
      setStage('method')
      return
    }
    if (pointsAllowed && !cardInAppAllowed) {
      setInAppMethod('points')
      startPointsCheckout()
      return
    }
    setInAppMethod('card')
    void startCheckout()
  }

  const startCheckout = async () => {
    if (!userId) {
      showToast({ type: 'error', message: t('cart.loginRequired') })
      return
    }
    if (!unpaidOrders.length || submitting) return
    setSubmitting(true)
    try {
      const res = isTable && tableNumber
        ? await api.createTableCheckout(storeId, tableNumber, userId)
        : await api.createSelfOrderCheckout(storeId, userId)
      if (res?.detail || !res?.clientSecret) {
        showToast({ type: 'error', message: res?.detail || t('checkout.failed') })
        return
      }
      setSession({
        clientSecret: res.clientSecret,
        paymentIntentId: res.paymentIntentId,
        connectedAccountId: res.connectedAccountId || null,
        totalAmount: res.totalAmount,
        orderIds: res.orderIds || [],
        isTable: !!(isTable && tableNumber),
        tableNumber,
        excludedStoreEnteredTotal: res.excludedStoreEnteredTotal || 0,
      })
      setStage('payment')
    } catch (e) {
      // #1801 現金のみ運用の店 (画面の店情報が古かった等) は backend が 409 で断る。
      // 理由をそのまま出し、選択を現金に倒す
      // #2190 前の決済で済んでいた、は失敗ではない (お会計は終わっている)
      const settledBefore = e instanceof ApiError && e.code === 'previous_payment_settled'
      showToast({ type: settledBefore ? 'info' : 'error', message: t(paymentDeclineI18nKey(e) ?? 'checkout.failed') })
      // 現金のみと分かったら会計の頭に戻し、アプリ払いの導線を閉じる
      if (isCashOnlyStoreError(e)) {
        setCashOnlyDetected(true)
        setStage('review')
      }
    } finally {
      setSubmitting(false)
    }
  }

  // #1800 YOKOSO ポイントで精算する。カードと違い confirm 段は無く、API が成功した
  // 時点で注文は paid。残高が足りなければ先に AutoTopUpSheet で不足分のチャージを
  // 承認してもらい、承認後に topup 付きで同じ API を呼ぶ (QR 決済 #1190 と同じ導線)。
  const executePointsCheckout = async (topup?: { topupAmount: number; topupFeeJpyc: number }) => {
    if (!userId) {
      showToast({ type: 'error', message: t('cart.loginRequired') })
      return
    }
    if (!unpaidOrders.length || submitting) return
    setSubmitting(true)
    try {
      const res: PointsCheckoutResult = isTable && tableNumber
        ? await api.createTablePointsCheckout(storeId, tableNumber, userId, topup, total)
        : await api.createSelfOrderPointsCheckout(storeId, userId, topup, total)
      setStage('confirming')
      // 残高を最新にする (ポイントが減った / 還元が付いた)
      await restoreSession().catch(() => { /* 表示だけの問題。決済は成立している */ })
      const store: Store = stores.find((s) => s.id === storeId)
        || ({ id: storeId, name: storeName, category: '' } as unknown as Store)
      useStore.setState({
        lastPaymentAmount: res.totalAmount,
        lastPaymentFee: 0,
        lastPaymentTxHash: null,
        lastPaymentExplorerLink: null,
        lastPaymentMethod: PAYMENT_METHOD_POINTS,
        lastPaymentTxId: res.transactionId,
        lastPaymentOrderIds: res.paid || [],
        lastTxTimestamp: Date.now(),
        currentStore: store,
      })
      showToast({ type: 'success', message: t('checkout.thanks') })
      if ((res.excludedStoreEnteredTotal ?? 0) > 0) {
        // #1535 店側起票の未払いは含まれていない — レジで別途払う旨を出す
        showToast({
          type: 'error',
          message: t('checkout.storeEnteredExcluded', {
            amount: (res.excludedStoreEnteredTotal ?? 0).toLocaleString(),
          }),
        })
      }
      if (onPaid) onPaid()
      else navigate('/payment/complete', { replace: true })
    } catch (e) {
      // 店が受けない / KYC 未完了 / 上限超過 / 残高不足は backend の code から文言を引く
      showToast({ type: 'error', message: t(paymentDeclineI18nKey(e) ?? 'checkout.failed') })
      // #2155 額が変わっていた (amount_changed) ら、読み直して正しい額を見せる
      if (e instanceof ApiError && e.code === 'amount_changed') {
        setTableBillNonce((n) => n + 1)
        const fresh = Number(e.detail?.totalAmount)
        if (Number.isFinite(fresh)) setServerTotal(fresh)
      }
      setStage('review')
    } finally {
      setSubmitting(false)
    }
  }

  const startPointsCheckout = () => {
    if (!userId) {
      showToast({ type: 'error', message: t('cart.loginRequired') })
      return
    }
    if (pointsShortfall > 0) {
      setShowAutoTopUp(true)
      return
    }
    void executePointsCheckout()
  }

  // #941/#942 現金会計: no Stripe call. Flag the unpaid orders as cash-pending
  // and show the register-guide popup. The store confirms receipt on their
  // side, which flips the bill paid via the Firestore listener.
  const startCashCheckout = async () => {
    // #1398 店頭払いはゲストのまま通す。金銭は動かず「レジで払う」と申告するだけで、
    // 確定は店員のレジ操作側にある。カード決済 (startCheckout) は従来どおり要ログイン。
    // 「自分の注文か」の判定はサーバ側 (ゲストIDで作った注文だけが対象)。
    if (!userId && !getGuestId()) {
      showToast({ type: 'error', message: t('cart.loginRequired') })
      return
    }
    if (!unpaidOrders.length || submitting) return
    setSubmitting(true)
    try {
      const res = isTable && tableNumber
        ? await api.createTableCashCheckout(storeId, tableNumber, userId || undefined)
        : await api.createSelfOrderCashCheckout(storeId, userId || undefined)
      if (res?.detail || !res?.orderIds) {
        showToast({ type: 'error', message: res?.detail || t('checkout.cashFailed') })
        return
      }
      setCashAmount(typeof res.totalAmount === 'number' ? res.totalAmount : null)
      setStage('cashGuide')
    } catch {
      showToast({ type: 'error', message: t('checkout.cashFailed') })
    } finally {
      setSubmitting(false)
    }
  }

  /** 確定を 1 回送る (#2190 同じ決済で何度送っても 1 回分) */
  const confirmOnce = async (paymentIntentId: string): Promise<CheckoutConfirmResult> => {
    if (!session) throw new Error('no checkout session')
    // Resolve the store from zustand so PaymentComplete / Review see the
    // same `currentStore` shape the /scan flow uses.
    // The browsable `stores` cache can be empty when the sheet is opened from
    // /my-orders (the user never visited the store list this session). Throwing
    // here used to fail the *confirm* step AFTER the card was already charged.
    // The confirm only needs id/name/category for the receipt record, so fall
    // back to the props instead of losing an already-paid bill.
    // Intentionally a partial Store: the confirm + receipt record only read
    // id/name/category, and PaymentComplete optional-chains the rest. `as
    // unknown` is needed because the partial doesn't overlap full Store.
    const store: Store = stores.find((s) => s.id === storeId)
      || ({ id: storeId, name: storeName, category: '' } as unknown as Store)

    if (session.isTable && session.tableNumber) {
      return payTableCheckout(session.totalAmount, store, paymentIntentId, session.orderIds, session.tableNumber)
    }
    return paySelfOrderCheckout(session.totalAmount, store, paymentIntentId, session.orderIds)
  }

  /**
   * #2190 カードが通った後の確定。落ちても (通信が切れた・混んでいた) カードの部品には戻さず、同じ決済で
   * 確定をやり直す。やり直しても確定できなければ「決済は完了している可能性があります」と出して、払い直させない。
   */
  const handlePaymentSuccess = async (paymentIntentId: string) => {
    if (!session || !userId) return
    setConfirmingPi(paymentIntentId)
    setVerifyFailed(false)
    setStage('confirming')
    for (let attempt = 0; ; attempt++) {
      try {
        const result = await confirmOnce(paymentIntentId)
        if (!alive.current) return
        if (result?.outcome === 'duplicate_refunded') {
          setDuplicateAmount(result.refundedAmount ?? session.totalAmount)
          setStage('duplicate')
          return
        }
        showToast({ type: 'success', message: t('checkout.thanks') })
        if (onPaid) {
          onPaid()
        } else {
          // Default flow: receipt page + review/share — same as /scan flow.
          navigate('/payment/complete', { replace: true })
        }
        return
      } catch (e) {
        console.error('[CheckoutSheet] confirm failed:', e)
        // やり直して変わりうるのは通信断・5xx だけ。4xx は何度送っても同じなので、すぐ「確かめられない」に
        if (!isRetryable(e) || attempt >= CONFIRM_RETRY_DELAYS_MS.length) break
        setStage('verifying')
        await new Promise((r) => setTimeout(r, CONFIRM_RETRY_DELAYS_MS[attempt]))
        if (!alive.current) return
      }
    }
    setStage('verifying')
    setVerifyFailed(true)
  }

  // #2190 確定の途中 (確定中・やり直している間) は閉じさせない。閉じても webhook が払い済みにするが、
  // 閉じて会計し直すと「処理中」で止まるだけで、お客様には何が起きたか分からない
  const closable = !(stage === 'confirming' || (stage === 'verifying' && !verifyFailed)
    || (stage === 'payment' && cardBusy))
  const close = () => { if (closable) onClose() }

  return (
    <>
    {/* #1800 不足分チャージの承認シート。外側の div の onClick (=閉じる) に
      * バブルさせないため、シートの兄弟として置く */}
    <AutoTopUpSheet
      open={showAutoTopUp}
      breakdown={topupBreakdownFor(balance, total)}
      storeName={storeName}
      onCancel={() => setShowAutoTopUp(false)}
      onConfirm={async () => {
        setShowAutoTopUp(false)
        await executePointsCheckout(topupParamsFor(balance, total))
      }}
    />
    <div
      className="fixed inset-0 z-[90] flex items-end justify-center bg-black/50 animate-fade-in"
      onClick={close}
    >
      <div
        className="relative w-full max-w-[430px] bg-white rounded-t-2xl p-6 pb-8 animate-slide-up max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {closable && (
          <button
            onClick={close}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 tap-effect"
            aria-label="close"
          >
            <X size={18} className="text-text-secondary" />
          </button>
        )}

        {stage === 'review' && (
          <>
            <div className="flex items-center gap-2 mb-1">
              <Receipt size={18} className="text-yokoso-red" />
              <h3 className="font-display font-semibold text-lg text-text-primary">
                {t('checkout.title')}
              </h3>
            </div>
            <p className="font-body text-xs text-text-tertiary mb-4">{storeName}</p>

            {/* #1535 卓の伝票 = 1 つのリスト (自分/友達/店員が入力タグ + 注文時刻)。
               * 状態 (受付/調理中/提供済) でグループ分けし、「まだ来ていないもの」が
               * 会計前にひと目で数えられるようにする。「自分の分」選択時は自分以外の
               * 行を薄くし、支払い対象を示す。 */}
            <p className="font-body text-xs font-semibold text-text-secondary mb-2">
              {t(showTags ? 'checkout.tableSlipTitle' : 'checkout.orderListTitle')}
            </p>
            <div className="space-y-3">
              {BILL_STATUS_ORDER
                .map((s) => ({
                  status: s,
                  // 未知の状態は「受付」に寄せて必ずどこかのグループに出す (行を落とさない)
                  rows: billRows.filter((r) =>
                    (BILL_STATUS_ORDER.includes(r.status || '') ? r.status : 'received') === s),
                }))
                .filter((g) => g.rows.length > 0)
                .map((g) => (
                  <div key={g.status} className="space-y-2">
                    <div className="flex items-center gap-2 pt-1">
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-body font-semibold shrink-0 ${CHECKOUT_STATUS_COLOR[g.status] || 'bg-gray-100 text-text-secondary'}`}>
                        {t(`myOrders.status.${g.status}`)} ({g.rows.length})
                      </span>
                      <span className="h-px flex-1 bg-gray-100" />
                    </div>
                    {g.rows.map((row) => (
                      <div
                        key={row.id}
                        className={`rounded-xl border border-gray-100 p-3 ${!row.mine && !isTable ? 'opacity-40' : ''}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-1.5 min-w-0">
                            {showTags && (
                              <span className={`rounded-full px-2 py-0.5 text-[10px] font-body font-bold shrink-0 ${
                                row.mine
                                  ? 'bg-yokoso-red/10 text-yokoso-red'
                                  : row.staffEntered
                                    ? 'bg-amber-100 text-amber-700'
                                    : 'bg-blue-100 text-blue-700'
                              }`}>
                                {t(row.mine
                                  ? 'checkout.byMe'
                                  : row.staffEntered ? 'myOrders.staffPlaced' : 'myOrders.byFriend')}
                              </span>
                            )}
                            <span className="font-display text-xs font-semibold text-text-secondary shrink-0">
                              #{row.number}
                            </span>
                            {/* #1535 注文時刻 — 「頼んだ/頼んでない」で揉めないための記録 */}
                            {formatOrderTime(row.createdAt) && (
                              <span className="font-body text-[11px] text-text-tertiary shrink-0">
                                {formatOrderTime(row.createdAt)}
                              </span>
                            )}
                          </div>
                          <span className="font-body text-sm font-bold text-gold shrink-0">
                            ¥{row.totalAmount.toLocaleString()}
                          </span>
                        </div>
                        {/* #1539 (REQ-24) 品目は省略せず 1 品 1 行で全文表示 (店側カードと同じ形式) */}
                        <ul className="mt-1 space-y-0.5">
                          {row.items.map((item, idx) => (
                            <li
                              key={`${item.name}-${idx}`}
                              className="flex items-baseline justify-between gap-2"
                            >
                              {/* #1857 何に払うのかを客が読めるようにする */}
                              <span className="min-w-0 flex-1">
                                <span className="block font-body text-xs text-text-secondary">
                                  {itemNameLines(item).primary}
                                </span>
                                {itemNameLines(item).secondary && (
                                  <span className="block font-jp text-[11px] text-text-tertiary">
                                    {itemNameLines(item).secondary}
                                  </span>
                                )}
                                <VariationLine item={item} />
                              </span>
                              <span className="font-body text-[11px] text-text-tertiary shrink-0">
                                × {item.quantity}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                ))}
            </div>

            {/* #956 Phase 2: 支払い範囲 — 自分の分 / テーブル全員分（他アカウントの
               * 未払いがある時だけ表示） */}
            {tableOption && tableBill && (
              <div className="mt-4">
                <p className="mb-2 font-body text-xs font-semibold text-text-secondary">
                  {t('checkout.scopeTitle')}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => { if (!forceTable) setScope('self') }}
                    disabled={forceTable}
                    className={`h-16 rounded-xl border-2 flex flex-col items-center justify-center gap-0.5 font-display text-sm font-semibold tap-effect ${
                      !isTable
                        ? 'border-yokoso-red bg-yokoso-red/5 text-yokoso-red'
                        : 'border-gray-200 text-text-secondary'
                    } ${forceTable ? 'opacity-40' : ''}`}
                  >
                    <Receipt size={16} />
                    <span>{t('checkout.scopeSelf')}</span>
                    {/* #2155 選んだときに払う額 (卓を締めるならお通し・席料込み)。下の合計と同じ */}
                    <span className="text-xs font-body">
                      ¥{(ownTotal + selfExtras).toLocaleString()}
                    </span>
                  </button>
                  <button
                    onClick={() => setScope('table')}
                    className={`h-16 rounded-xl border-2 flex flex-col items-center justify-center gap-0.5 font-display text-sm font-semibold tap-effect ${
                      isTable
                        ? 'border-yokoso-red bg-yokoso-red/5 text-yokoso-red'
                        : 'border-gray-200 text-text-secondary'
                    }`}
                  >
                    <Users size={16} />
                    <span>{t('checkout.scopeTable')}</span>
                    <span className="text-xs font-body">¥{(tableBill.totalAmount + tableExtras).toLocaleString()}</span>
                  </button>
                </div>
                {/* #977 店員入力がある卓は「テーブル全員分」固定。理由を明示。 */}
                {forceTable ? (
                  <p className="mt-2 font-body text-[11px] text-amber-700 text-center leading-relaxed">
                    {t('checkout.staffForceTableNote')}
                  </p>
                ) : isTable ? (
                  <p className="mt-2 font-body text-[11px] text-text-tertiary text-center">
                    {t('checkout.scopeTableHint', { table: tableNumber || '', count: tableBill.orderCount })}
                  </p>
                ) : null}
              </div>
            )}

            <div className="mt-5 border-t border-gray-100 pt-4">
              {/* #1535 テーブル全員分の内訳。myTotal は「この卓の」自分の分 —
                 * 「自分の分」ボタンは店舗内の自分の未払いを卓をまたいで合算するため、
                 * 別卓の未払いがあると金額が一致しない。ラベルの「この卓」で明示する。
                 * 旧サーバ応答 (myTotal 無し) では出さない。 */}
              {isTable && tableBill && typeof tableBill.myTotal === 'number' && (
                <div className="mb-2 space-y-1">
                  <div className="flex items-center justify-between font-body text-xs text-text-secondary">
                    <span>{t('checkout.breakdownMine')}</span>
                    <span>¥{tableBill.myTotal.toLocaleString()}</span>
                  </div>
                  {(tableBill.othersTotal || 0) > 0 && (
                    <div className="flex items-center justify-between font-body text-xs text-text-secondary">
                      <span>{t('checkout.breakdownOthers')}</span>
                      <span>¥{(tableBill.othersTotal || 0).toLocaleString()}</span>
                    </div>
                  )}
                  {(tableBill.staffTotal || 0) > 0 && (
                    <div className="flex items-center justify-between font-body text-xs text-text-secondary">
                      <span>{t('checkout.breakdownStaff')}</span>
                      <span>¥{(tableBill.staffTotal || 0).toLocaleString()}</span>
                    </div>
                  )}
                </div>
              )}
              {/* #2155 お通し・席料 (卓を締める支払いのとき)。名前はお店が付けたもの */}
              {extrasTotal > 0 && (tableBill?.autoExtras || []).map((x) => (
                <div key={x.presetId || x.name} className="mb-1 flex items-center justify-between font-body text-xs text-text-secondary">
                  <span>{x.name} ×{x.quantity}</span>
                  <span>¥{(x.price * x.quantity).toLocaleString()}</span>
                </div>
              ))}
              <div className="flex items-center justify-between">
                <span className="font-body text-sm text-text-secondary">{t('checkout.total')}</span>
                <span className="font-display text-2xl font-bold text-text-primary">
                  ¥{total.toLocaleString()}
                </span>
              </div>
            </div>

            {/* #1044 割り勘表示 — 表示・計算のみで決済フローには影響しない */}
            <div className="mt-3">
              <button
                onClick={() => setSplitEnabled((v) => !v)}
                className={`w-full h-11 rounded-xl border-2 flex items-center justify-center gap-2 font-display text-sm font-semibold tap-effect ${
                  splitEnabled
                    ? 'border-yokoso-red bg-yokoso-red/5 text-yokoso-red'
                    : 'border-gray-200 text-text-secondary'
                }`}
              >
                <Users size={16} />
                {t('checkout.splitBillToggle')}
              </button>

              {splitEnabled && (
                <div className="mt-3 rounded-xl bg-gray-50 border border-gray-100 p-4">
                  <p className="text-center font-body text-xs font-semibold text-text-secondary mb-2">
                    {t('checkout.splitBillCount')}
                  </p>
                  <div className="flex items-center justify-center gap-4">
                    <button
                      onClick={() => setSplitCount((c) => Math.max(SPLIT_COUNT_MIN, c - 1))}
                      disabled={splitCount <= SPLIT_COUNT_MIN}
                      className="w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center tap-effect disabled:opacity-30"
                    >
                      <Minus size={16} className="text-text-primary" />
                    </button>
                    <span className="font-display text-2xl font-bold text-text-primary w-12 text-center tabular-nums">
                      {splitCount}
                    </span>
                    <button
                      onClick={() => setSplitCount((c) => Math.min(SPLIT_COUNT_MAX, c + 1))}
                      disabled={splitCount >= SPLIT_COUNT_MAX}
                      className="w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center tap-effect disabled:opacity-30"
                    >
                      <Plus size={16} className="text-text-primary" />
                    </button>
                  </div>
                  <div className="mt-3 border-t border-gray-200 pt-3 flex items-center justify-between">
                    <span className="font-body text-xs text-text-secondary">{t('checkout.splitBillPerPerson')}</span>
                    <span className="font-display text-lg font-bold text-yokoso-red">
                      ¥{Math.ceil(total / splitCount).toLocaleString()}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* #1805 支払いの分岐は 2 段。
              *   1 段目「アプリで支払う / レジで支払う」= **web 決済か店頭払いか**
              *   2 段目 (アプリを選んだとき) 「カード / YOKOSO 通貨」
              * カードも YOKOSO 通貨もアプリ内で完結するので、同じ列に並べる。
              * Apple Pay / Google Pay はカードを選んだ後に Stripe の画面が自動で出す (#978/#983)。
              * #1801 店の情報が届くまでは出さない (誤って「現金のみ」と見せない)。 */}
            {!paymentOptionsKnown ? (
              <p className="mt-4 text-center font-body text-xs text-text-tertiary">
                {t('common.loading')}
              </p>
            ) : inAppAvailable ? (
              <>
                <p className="mt-4 mb-2 font-body text-xs font-semibold text-text-secondary">
                  {t('checkout.methodTitle')}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={goInApp}
                    disabled={submitting || !unpaidOrders.length || !paymentOptionsKnown}
                    data-testid="checkout-channel-in-app"
                    className="h-14 rounded-xl border-2 border-yokoso-red bg-yokoso-red/5 text-yokoso-red flex flex-col items-center justify-center gap-0.5 tap-effect disabled:opacity-50"
                  >
                    <span className="flex items-center gap-1.5 font-display text-sm font-semibold">
                      <Smartphone size={15} />
                      {t('checkout.methodInApp')}
                    </span>
                    <span className="font-body text-[10px] opacity-70">
                      {/* 使える手段を補足に出す。ポイントだけの店では「YOKOSO 通貨」だけ */}
                      {!cardInAppAllowed
                        ? t('payment.methodPoints')
                        : pointsAllowed
                          ? t('checkout.methodInAppSubPoints')
                          : t('checkout.methodInAppSub')}
                    </span>
                  </button>
                  <button
                    onClick={() => { void startCashCheckout() }}
                    disabled={submitting || !unpaidOrders.length}
                    className="h-14 rounded-xl border-2 border-gray-200 text-text-secondary flex flex-col items-center justify-center gap-0.5 tap-effect disabled:opacity-50"
                  >
                    <span className="flex items-center gap-1.5 font-display text-sm font-semibold">
                      <Banknote size={15} />
                      {t('checkout.methodRegister')}
                    </span>
                    <span className="font-body text-[10px] opacity-70">
                      {payAtRegister ? t('checkout.methodRegisterSub') : t('checkout.methodCashSub')}
                    </span>
                  </button>
                </div>

                {/* #1805 2 段目 (カード / YOKOSO 通貨) は次の画面へ。ここに積むと
                  * 1 段目と同時に光って、どちらが効いているのか読めなくなる */}
              </>
            ) : (
              <div className="mt-4 rounded-xl bg-matcha/10 border border-matcha/30 px-3 py-2.5 flex items-center gap-2">
                <Banknote size={16} className="text-matcha shrink-0" />
                <span className="font-body text-xs text-text-secondary leading-relaxed">
                  {t('checkout.cashOnlyNote')}
                </span>
              </div>
            )}

            {/* #1805 アプリが使える店では上の 2 つが**行き先**なので、下にもう 1 つ
              * ボタンを置かない (押す場所が 2 か所あると、どちらが本番か迷う)。
              * 現金しか使えない店だけは選ぶものが無いので、ここで払う */}
            {!inAppAvailable && (
              <button
                onClick={startCashCheckout}
                disabled={submitting || !unpaidOrders.length}
                className={`mt-4 w-full h-14 rounded-xl font-display font-semibold text-base flex items-center justify-center gap-2 ${
                  submitting || !unpaidOrders.length
                    ? 'bg-gray-300 text-white'
                    : 'bg-yokoso-red text-white tap-effect active:scale-[0.98] transition-transform'
                }`}
              >
                {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
                {t('checkout.payWithCash')}
              </button>
            )}
          </>
        )}

        {/* #1805 2 段目。「アプリで支払う」を選んだ人にだけ、その中身を聞く */}
        {stage === 'method' && (
          <>
            <button
              onClick={() => setStage('review')}
              className="mb-2 inline-flex items-center gap-1 font-body text-xs text-text-secondary tap-effect"
            >
              <ChevronLeft size={14} />
              {t('checkout.title')}
            </button>
            <div className="flex items-center gap-2 mb-1">
              <Smartphone size={18} className="text-yokoso-red" />
              <h3 className="font-display font-semibold text-lg text-text-primary">
                {t('checkout.methodInApp')}
              </h3>
            </div>
            <p className="font-body text-xs text-text-tertiary mb-4">
              ¥{total.toLocaleString()} — {storeName}
            </p>

            <PayMethodToggle
              method={effectiveInApp}
              onChange={setInAppMethod}
              cardAllowed={!!cardInAppAllowed}
              balance={balance}
              amount={total}
            />

            <button
              onClick={effectiveInApp === 'points' ? startPointsCheckout : startCheckout}
              disabled={submitting || !unpaidOrders.length}
              className={`mt-2 w-full h-14 rounded-xl font-display font-semibold text-base flex items-center justify-center gap-2 ${
                submitting || !unpaidOrders.length
                  ? 'bg-gray-300 text-white'
                  : 'bg-yokoso-red text-white tap-effect active:scale-[0.98] transition-transform'
              }`}
            >
              {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
              {t('checkout.payNow')}
            </button>
          </>
        )}

        {stage === 'cashGuide' && (
          <div className="flex flex-col items-center text-center py-6">
            <div className="w-16 h-16 rounded-full bg-yokoso-red/10 flex items-center justify-center mb-4">
              <StoreIcon size={30} className="text-yokoso-red" />
            </div>
            <h3 className="font-display font-bold text-xl text-text-primary">
              {staffBringsBill ? t('checkout.cashGuideTitleStaffBrings') : t('checkout.cashGuideTitle')}
            </h3>
            <p className="mt-2 font-body text-sm text-text-secondary leading-relaxed px-2">
              {/* #2344 カードが使えると言ってよいのは payAtRegister の店だけ
                * (現金のみの店で「カードもご利用いただけます」と並べない) */}
              {staffBringsBill
                ? t(payAtRegister
                  ? 'checkout.cashGuideDescStaffBrings'
                  : 'checkout.cashGuideDescStaffBringsCash')
                : payAtRegister
                  ? t('checkout.cashGuideDescRegister')
                  : t('checkout.cashGuideDesc')}
            </p>
            <div className="mt-5 w-full rounded-xl bg-gray-50 border border-gray-100 p-4 flex items-center justify-between">
              <span className="font-body text-sm text-text-secondary">{t('checkout.cashGuideAmount')}</span>
              <span className="font-display text-2xl font-bold text-text-primary">
                ¥{cashGuideAmount.toLocaleString()}
              </span>
            </div>
            <button
              onClick={() => {
                onClose()
                navigate('/my-orders', { replace: true })
              }}
              className="mt-6 w-full py-3.5 rounded-xl bg-yokoso-red text-white font-display font-semibold text-base tap-effect active:scale-[0.98] transition-transform"
            >
              {t('checkout.cashGuideDone')}
            </button>
          </div>
        )}

        {stage === 'payment' && session && (
          <>
            <h3 className="font-display font-semibold text-lg text-text-primary mb-4">
              {t('checkout.payment')}
            </h3>
            {/* #1535 店側起票 (店員入力・放題プラン) の未払いはこの支払いに入らない。
              * 黙って除外すると「全部払えた」と誤解し、レジでの回収漏れになる */}
            {(session.excludedStoreEnteredTotal ?? 0) > 0 && (
              <p className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 font-body text-xs leading-relaxed text-amber-800">
                {t('checkout.storeEnteredExcluded', {
                  amount: (session.excludedStoreEnteredTotal ?? 0).toLocaleString(),
                })}
              </p>
            )}
            <StripeCheckout
              clientSecret={session.clientSecret}
              amount={session.totalAmount}
              storeName={storeName}
              connectedAccountId={session.connectedAccountId}
              onSuccess={handlePaymentSuccess}
              onBusyChange={setCardBusy}
              // #1805 カードをやめた人が戻るのは支払い方法の選択 (会計の頭ではない)
              onCancel={() => setStage(cardInAppAllowed && pointsAllowed ? 'method' : 'review')}
            />
          </>
        )}

        {stage === 'confirming' && (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <Loader2 size={28} className="animate-spin text-yokoso-red" />
            <p className="font-body text-sm text-text-secondary">{t('checkout.confirming')}</p>
          </div>
        )}

        {/* #2190 カードは通ったが確定が届かない。払い直させない */}
        {stage === 'verifying' && (
          <div className="flex flex-col items-center justify-center py-8 gap-3 text-center" role="status">
            {!verifyFailed && <Loader2 size={28} className="animate-spin text-gold" />}
            <p className="font-display text-base font-semibold text-text-primary">{t('checkout.verifyingTitle')}</p>
            <p className="font-body text-sm text-text-secondary">
              {t(verifyFailed ? 'checkout.verifyFailed' : 'checkout.verifying')}
            </p>
            {verifyFailed && confirmingPi && (
              <div className="mt-2 flex w-full flex-col gap-2">
                <button
                  onClick={() => void handlePaymentSuccess(confirmingPi)}
                  className="tap-effect h-12 w-full rounded-xl bg-navy font-display text-sm font-semibold text-white"
                >
                  {t('checkout.verifyAgain')}
                </button>
                <button
                  onClick={() => { onClose(); navigate('/my-orders') }}
                  className="tap-effect h-12 w-full rounded-xl border border-gray-200 font-body text-sm font-medium text-text-secondary"
                >
                  {t('checkout.seeOrders')}
                </button>
              </div>
            )}
          </div>
        )}

        {/* #2190 先にほかの支払いで払われていた。この決済はカードに返金する */}
        {stage === 'duplicate' && (
          <div className="flex flex-col items-center justify-center py-8 gap-3 text-center" role="status">
            <p className="font-display text-base font-semibold text-text-primary">{t('checkout.duplicateTitle')}</p>
            <p className="font-body text-sm text-text-secondary">
              {t('checkout.duplicateRefunded', { amount: duplicateAmount.toLocaleString() })}
            </p>
            <button
              onClick={onClose}
              className="tap-effect mt-2 h-12 w-full rounded-xl bg-navy font-display text-sm font-semibold text-white"
            >
              {t('common.close')}
            </button>
          </div>
        )}
      </div>
    </div>
    </>
  )
}
